<template>
  <v-dialog v-model="show" max-width="600" persistent>
    <v-card rounded="xl">
      <v-card-title class="text-h6 font-weight-bold pt-5 px-6">
        Crear Orden de Venta
      </v-card-title>
      <v-card-subtitle class="px-6 pb-0">
        Completa los datos del comprador para generar la orden
      </v-card-subtitle>

      <v-divider class="mt-3"></v-divider>

      <v-card-text class="px-6 pt-4">
        <v-form ref="form" v-model="validForm" lazy-validation>
          <!-- Email del comprador -->
          <v-text-field
            v-model="form.email"
            label="Correo electrónico del comprador"
            outlined
            dense
            rounded
            :rules="emailRules"
            :error-messages="submemberMessage"
            @input="onEmailInput"
            prepend-inner-icon="mdi-email-outline"
            class="mb-1"
          >
            <template v-slot:append>
              <v-progress-circular
                v-if="lookingUpEmail"
                indeterminate
                size="18"
                width="2"
                color="primary"
              ></v-progress-circular>
              <v-icon v-else-if="isSubmember" color="red">mdi-account-cancel</v-icon>
              <v-icon v-else-if="memberFound" color="green">mdi-account-check</v-icon>
              <v-icon v-else-if="emailChecked" color="grey">mdi-account-plus-outline</v-icon>
            </template>
          </v-text-field>

          <!-- Indicador de tipo de comprador -->
          <div class="mb-3" v-if="emailChecked && !isSubmember">
            <v-chip
              v-if="memberFound"
              small
              color="green lighten-5"
              text-color="green darken-2"
              class="font-weight-medium"
            >
              <v-icon left small>mdi-check-circle</v-icon>
              Miembro encontrado
            </v-chip>
            <v-chip
              v-if="memberFound && hasExistingOrder"
              small
              color="orange lighten-5"
              text-color="orange darken-3"
              class="font-weight-medium ml-2"
            >
              <v-icon left small>mdi-alert-outline</v-icon>
              Este miembro ya tiene una orden creada
            </v-chip>
            <v-chip
              v-if="!memberFound"
              small
              color="red lighten-5"
              text-color="red darken-2"
              class="font-weight-medium"
            >
              <v-icon left small>mdi-alert-circle-outline</v-icon>
              {{ context === 'membership' ? 'Este correo no pertenece a un miembro registrado' : 'Prospecto externo' }}
            </v-chip>
          </div>

          <!-- Nombre -->
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Nombre"
                outlined
                dense
                rounded
                :rules="requiredRules"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.lastName"
                label="Apellido"
                outlined
                dense
                rounded
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Tipo de membresía o ticket -->
          <v-select
            v-model="form.itemId"
            :items="itemOptions"
            :label="context === 'membership' ? 'Tipo de membresía' : 'Tipo de ticket'"
            outlined
            dense
            rounded
            :rules="requiredRules"
            item-value="value"
            item-text="label"
            no-data-text="No hay opciones disponibles"
            @change="onItemSelected"
          >
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-on="on" v-bind="attrs" :disabled="item.disabled">
                <v-list-item-content>
                  <v-list-item-title
                    :class="item.disabled ? 'grey--text' : ''"
                  >
                    {{ item.name }}
                    <v-chip
                      v-if="item.disabled"
                      x-small
                      color="grey lighten-3"
                      text-color="grey"
                      class="ml-2"
                    >sin costo</v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="!item.disabled">
                    {{ formatPrice(item.price) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>

          <!-- Total (solo lectura) -->
          <v-text-field
            :value="totalDisplay"
            label="Total"
            outlined
            dense
            rounded
            readonly
            prepend-inner-icon="mdi-currency-usd"
            color="grey"
            class="mb-1"
          ></v-text-field>


        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          rounded
          color="grey darken-1"
          class="font-weight-bold"
          @click="close"
          :disabled="loading"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          rounded
          class="font-weight-bold white--text"
          @click="submit"
          :loading="loading"
          :disabled="!validForm || (context === 'membership' && emailChecked && !memberFound)"
        >
          Crear Orden
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Diálogo de confirmación: orden existente -->
    <v-dialog v-model="confirmReplaceDialog" max-width="440" persistent>
      <v-card rounded="xl">
        <v-card-text class="text-center pt-6 pb-2 px-6">
          <v-icon color="orange darken-2" size="56" class="mb-3">mdi-alert-circle-outline</v-icon>
          <p class="text-body-1 font-weight-medium mt-2">
            Este miembro ya tiene previamente una orden creada, si finalizas la creación de esta orden, reemplazará la orden anterior.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text rounded color="grey darken-1" @click="confirmReplaceDialog = false">Cancelar</v-btn>
          <v-btn color="orange darken-2" rounded class="white--text font-weight-bold" @click="confirmAndSubmit" :loading="loading">
            Entiendo, crear orden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { formatPrice } from "@/utils";
import { validations } from "@/share/validations.js";

export default {
  name: "CreateSalesOrderDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    affiliationId: {
      type: String,
      default: null,
    },
    eventId: {
      type: String,
      default: null,
    },
    context: {
      type: String,
      default: "membership", // 'membership' | 'event'
    },
  },
  data() {
    return {
      validForm: false,
      loading: false,
      lookingUpEmail: false,
      emailChecked: false,
      memberFound: false,
      isSubmember: false,
      hasExistingOrder: false,
      confirmReplaceDialog: false,
      emailDebounceTimer: null,
      itemOptions: [],
      selectedItem: null,
      form: {
        email: "",
        name: "",
        lastName: "",
        itemId: null,
      },
      requiredRules: [(v) => !!v || "Campo requerido"],
      emailRules: validations.emailValidation({ required: true }),

    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    totalDisplay() {
      if (!this.selectedItem) return "—";
      return formatPrice(this.selectedItem.price || 0);
    },
    submemberMessage() {
      if (this.context === 'membership' && this.isSubmember) {
        return 'El usuario es submiembro/colaborador, las órdenes de venta se crean únicamente para miembros titulares';
      }
      return [];
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.loadItems();
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    formatPrice,
    async loadItems() {
      try {
        if (this.context === "membership" && this.affiliationId) {
          const { data } = await this.axios.get(
            `/affiliation-types/${this.affiliationId}`
          );
          const types = data.affiliationTypes || data || [];
          this.itemOptions = types.map((t) => ({
            value: t._id,
            label: t.price > 0 ? `${t.name} — ${formatPrice(t.price)}` : t.name,
            name: t.name,
            price: t.price || 0,
            disabled: !t.price || t.price <= 0,
          }));
        } else if (this.context === "event" && this.eventId) {
          const { data } = await this.axios.get(`/tickets/${this.eventId}`);
          const tickets = data.tickets || data || [];
          this.itemOptions = tickets
            .filter((t) => t.status === "ACTIVE")
            .map((t) => ({
              value: t._id,
              label: `${t.name} — ${formatPrice(t.price)}`,
              name: t.name,
              price: t.price || 0,
              disabled: false,
            }));
        }
      } catch (e) {
        console.error("Error loading items:", e);
      }
    },
    onItemSelected(id) {
      this.selectedItem = this.itemOptions.find((o) => o.value === id) || null;
    },
    onEmailInput(val) {
      this.emailChecked = false;
      this.memberFound = false;
      this.isSubmember = false;
      this.hasExistingOrder = false;
      clearTimeout(this.emailDebounceTimer);
      if (!val || val.length < 5) return;
      this.emailDebounceTimer = setTimeout(() => {
        this.lookupMember(val);
      }, 600);
    },
    async lookupMember(email) {
      this.lookingUpEmail = true;
      try {
        const params = { email };
        if (this.affiliationId) params.affiliationId = this.affiliationId;
        if (this.eventId) params.eventId = this.eventId;
        const { data } = await this.axios.get("/users/member-lookup", {
          params,
        });
        if (data.found) {
          this.isSubmember = !!data.isSubmember;
          // Para contexto de membresía, los submiembros NO se consideran
          // miembros válidos para crear órdenes de venta.
          this.memberFound =
            this.context === 'membership' ? !data.isSubmember : true;
          this.hasExistingOrder = !!data.hasExistingOrder;
          if (!this.isSubmember) {
            if (!this.form.name) this.form.name = data.name || "";
            if (!this.form.lastName) this.form.lastName = data.lastName || "";
          }
        } else {
          this.memberFound = false;
          this.isSubmember = false;
          this.hasExistingOrder = false;
        }
      } catch {
        this.memberFound = false;
        this.isSubmember = false;
      } finally {
        this.lookingUpEmail = false;
        this.emailChecked = true;
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      if (this.context === 'membership' && this.isSubmember) return;
      if (this.context === 'membership' && !this.memberFound) return;

      if (this.hasExistingOrder && !this.confirmReplaceDialog) {
        this.confirmReplaceDialog = true;
        return;
      }

      await this.createOrder();
    },
    async confirmAndSubmit() {
      this.confirmReplaceDialog = false;
      await this.createOrder();
    },
    async createOrder() {

      const total = this.selectedItem ? this.selectedItem.price : 0;
      const payload = {
        name: this.form.name,
        lastName: this.form.lastName,
        email: this.form.email,
        total,
      };

      if (this.context === "membership") {
        payload.affiliationId = this.affiliationId;
        payload.affiliationTypeId = this.form.itemId;
      } else {
        payload.eventId = this.eventId;
        payload.ticketId = this.form.itemId;
      }

      this.loading = true;
      try {
        const { data } = await this.axios.post("/orders/admin/create", payload);
        this.$emit("created", data.order);
        this.close();
      } catch (e) {
        const message =
          e?.response?.data?.error?.message ||
          "Ha ocurrido un error al crear la orden.";
        this.$emit("error", message);
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.show = false;
    },
    resetForm() {
      this.form = { email: "", name: "", lastName: "", itemId: null };
      this.selectedItem = null;
      this.emailChecked = false;
      this.memberFound = false;
      this.isSubmember = false;
      this.hasExistingOrder = false;
      this.confirmReplaceDialog = false;
      this.itemOptions = [];
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
  },
};
</script>
